// C.1 — батч для юнитов Деуса (v6 ID).
// Реализует понятные эффекты: damage, heal, buff, silence, influence_gain,
// triggers (ally_died → +self perm, turn_start, ally_summoned, unit_attack,
// unit_takes_damage), deathrattles.
//
// Сложные карты (mind_control, mass_sacrifice, free_summons_from_stand,
// next_n_summons_discount, mega_sdelka) — стабы.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { applyPermanentBuff, applyTemporaryBuff } from '../../../buffs.js';
import { applyDamage } from '../../../combat.js';
import { healUnit } from '../../livlife/heal.js';
import { applySilence } from '../../../keywords/silence.js';
import { addPermanentKeyword } from '../../../keywords/permanent.js';
import { applyImmuneFirstHit, removeImmunity } from '../../../keywords/immune.js';
import { KW } from '../../../keywords.js';
import { resolveTarget } from '../../../effects/targets.js';
import { addInfluence } from '../influence.js';
import { summonUnit } from '../../global/summon.js';
import {
  registerOnUnitDiedExtraHandler,
  registerUnitSummonedExtraHandler,
  registerOnEndOfTurnExtraHandler,
  registerOnTurnStartExtraHandler
} from '../hooks.js';
import { registerC1Order } from '../../global/c1/order-registry.js';
import { registerOnAttackTrigger } from '../../livlife/on-attack-triggers.js';
import { registerOnDamageTakenTrigger } from '../../livlife/on-damage-triggers.js';
import { mindControlUnit } from '../../../effects/mind-control.js';

const STAZHER_ID = 'deus_stazher';
const ZARAZHENY_ID = 'deus_zarazheny_iskinom';

function findFreeSlot(player) {
  if (!player?.field) return null;
  for (let line = 0; line < 2; line++) {
    const row = player.field[line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s] === null) return { line, slot: s };
    }
  }
  return null;
}

// ============================================================
// === ORDERS                                                  ===
// ============================================================

// Damage Orders
registerC1Order('deus_inf_likvidator', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false, reason: 'no_target' };
  applyDamage(t, 2, { state, source });
  return { ok: true };
});
registerImplementedAbility('deus_inf_likvidator', 'order');

registerC1Order('deus_korpdirector', (state, source) => {
  const targets = resolveTarget(state, source, 'enemy_aoe_line');
  for (const t of targets) applyDamage(t, 1, { state, source });
  return { ok: true, hit: targets.length };
});
registerImplementedAbility('deus_korpdirector', 'order');

registerC1Order('deus_korpreid', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false, reason: 'no_target' };
  applyDamage(t, 4, { state, source });
  return { ok: true };
});
registerImplementedAbility('deus_korpreid', 'order');

registerC1Order('deus_rasstrelnaya_komanda', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false, reason: 'no_target' };
  applyDamage(t, 6, { state, source });
  return { ok: true };
});
registerImplementedAbility('deus_rasstrelnaya_komanda', 'order');

registerC1Order('deus_vygovor', (state, source, opts) => {
  const t = opts?.target;
  if (t) applyDamage(t, 2, { state, source });
  // + summon Stazher.
  const ownerIdx = source.ownerId;
  const owner = state.players?.[ownerIdx];
  const slot = findFreeSlot(owner);
  if (slot) summonUnit(state, ownerIdx, STAZHER_ID, slot.line, slot.slot);
  return { ok: true };
});
registerImplementedAbility('deus_vygovor', 'order');

registerC1Order('deus_lichnoe_uchastie', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false, reason: 'no_target' };
  const heroAtk = state.players?.[source.ownerId]?.hero?.atk ?? 0;
  if (heroAtk <= 0) return { ok: true, dmg: 0 };
  applyDamage(t, heroAtk, { state, source, pierce: true });
  return { ok: true, dmg: heroAtk };
});
registerImplementedAbility('deus_lichnoe_uchastie', 'order');

// Heal Orders
registerC1Order('deus_medik', (state, source) => {
  const t = resolveTarget(state, source, 'ally_lowest_hp')[0];
  if (t) healUnit(state, t, 2, source);
  return { ok: true };
});
registerImplementedAbility('deus_medik', 'order');

registerC1Order('deus_medstrahovka', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (hero) healUnit(state, hero, 4, source);
  return { ok: true };
});
registerImplementedAbility('deus_medstrahovka', 'order');

registerC1Order('deus_reabilitatsiya', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  const cur = hero.hp || 0;
  const max = hero.maxHp || hero.base?.hp || cur;
  if (cur < max) healUnit(state, hero, max - cur, source);
  return { ok: true };
});
registerImplementedAbility('deus_reabilitatsiya', 'order');

// Buff Orders
registerC1Order('deus_restrukturizatsiya', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false };
  applyPermanentBuff(state, t, { atk: 0, armor: 1, hp: 1 }, source.id, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_restrukturizatsiya', 'order');

registerC1Order('deus_takticheskiy_shturm', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  hero.base.atk += 2; hero.current.atk += 2;
  return { ok: true };
});
registerImplementedAbility('deus_takticheskiy_shturm', 'order');

registerC1Order('deus_boevoe_snaryazhenie', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  hero.base.atk += 2; hero.current.atk += 2;
  addPermanentKeyword(state, hero, KW.PIERCE, source.id, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_boevoe_snaryazhenie', 'order');

registerC1Order('deus_implanty_khishchnik', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  hero.base.atk += 3; hero.current.atk += 3;
  addPermanentKeyword(state, hero, KW.PIERCE, source.id, source.name_ru);
  addPermanentKeyword(state, hero, KW.FRENZY, source.id, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_implanty_khishchnik', 'order');

// Silence Orders
registerC1Order('deus_korp_shantazhist', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false };
  applySilence(state, t, source.id);
  return { ok: true };
});
registerImplementedAbility('deus_korp_shantazhist', 'order');

registerC1Order('deus_ukaz_soveta', (state, source, opts) => {
  const t = opts?.target;
  if (!t) return { ok: false };
  applySilence(state, t, source.id);
  return { ok: true };
});
registerImplementedAbility('deus_ukaz_soveta', 'order');

// Influence Orders
registerC1Order('deus_buhgalter', (state, source) => {
  addInfluence(state, source.ownerId, 1, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_buhgalter', 'order');

registerC1Order('deus_korp_finansist', (state, source) => {
  addInfluence(state, source.ownerId, 5, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_korp_finansist', 'order');

registerC1Order('deus_nalog_inspector', (state, source) => {
  addInfluence(state, source.ownerId, 2, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_nalog_inspector', 'order');

registerC1Order('deus_tayny_sovet', (state, source) => {
  const p = state.players?.[source.ownerId];
  if (!p) return { ok: false };
  // Подъём максимума Влияния (если есть отдельный параметр в Player).
  if (typeof p.influenceMax === 'number') p.influenceMax += 2;
  return { ok: true };
});
registerImplementedAbility('deus_tayny_sovet', 'order');

registerC1Order('deus_inf_posrednik', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  hero.base.hp += 2; hero.current.hp += 2;
  return { ok: true };
});
registerImplementedAbility('deus_inf_posrednik', 'order');

// Composite Orders
registerC1Order('deus_korp_sdelka', (state, source) => {
  addInfluence(state, source.ownerId, 3, source.name_ru);
  const owner = state.players?.[source.ownerId];
  const slot = findFreeSlot(owner);
  if (slot) summonUnit(state, source.ownerId, STAZHER_ID, slot.line, slot.slot);
  return { ok: true };
});
registerImplementedAbility('deus_korp_sdelka', 'order');

registerC1Order('deus_sliyanie', (state, source) => {
  const owner = state.players?.[source.ownerId];
  if (!owner) return { ok: false };
  // Draw 2.
  for (let i = 0; i < 2; i++) {
    if (owner.deck.length > 0 && owner.hand.length < 10) {
      owner.hand.push(owner.deck.shift());
    }
  }
  // 1 damage hero_self.
  if (owner.hero) applyDamage(owner.hero, 1, { state, source });
  return { ok: true };
});
registerImplementedAbility('deus_sliyanie', 'order');

registerC1Order('deus_podpis_smertnika', (state, source, opts) => {
  const t = opts?.target;
  if (t && t.ownerId === source.ownerId && t.isAlive && t.isAlive()) {
    applyDamage(t, 999, { state, source });
  }
  const owner = state.players?.[source.ownerId];
  for (let i = 0; i < 2; i++) {
    if (owner.deck.length > 0 && owner.hand.length < 10) {
      owner.hand.push(owner.deck.shift());
    }
  }
  return { ok: true };
});
registerImplementedAbility('deus_podpis_smertnika', 'order');

registerC1Order('deus_likvidatsiya_kontrakta', (state, source, opts) => {
  // destroy ally_choose_atk_le_2 + summon zarazheny + summon stazher.
  const t = opts?.target;
  if (t && t.ownerId === source.ownerId && (t.current?.atk ?? 99) <= 2) {
    applyDamage(t, 999, { state, source });
  }
  const owner = state.players?.[source.ownerId];
  for (const sumId of [ZARAZHENY_ID, STAZHER_ID]) {
    const slot = findFreeSlot(owner);
    if (slot) summonUnit(state, source.ownerId, sumId, slot.line, slot.slot);
  }
  return { ok: true };
});
registerImplementedAbility('deus_likvidatsiya_kontrakta', 'order');

// Give_keyword to hero (temp)
registerC1Order('deus_boevoy_implant', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  // Frenzy до конца следующего хода — упрощённо постоянно.
  addPermanentKeyword(state, hero, KW.FRENZY, source.id, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_boevoy_implant', 'order');

registerC1Order('deus_operativny_prikaz', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  addPermanentKeyword(state, hero, KW.PIERCE, source.id, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_operativny_prikaz', 'order');

registerC1Order('deus_peredovoy_shchit', (state, source) => {
  const hero = state.players?.[source.ownerId]?.hero;
  if (!hero) return { ok: false };
  applyImmuneFirstHit(state, hero, source.id);
  return { ok: true };
});
registerImplementedAbility('deus_peredovoy_shchit', 'order');

// Заражённый искином — Приказ «призови ещё одного Заражённого искином».
// Этап 80: был СТАБ (помечен implemented без логики) → копия не призывалась.
// summonUnit НЕ запускает Order призванной копии (как Знаток искинов выше),
// поэтому цепной реакции нет — ровно +1 юнит при выставлении оригинала.
registerC1Order('deus_zarazheny_iskinom', (state, source) => {
  const ownerIdx = source.ownerId;
  const owner = state.players?.[ownerIdx];
  const slot = findFreeSlot(owner);
  if (slot) {
    summonUnit(state, ownerIdx, ZARAZHENY_ID, slot.line, slot.slot);
    state.log.add(`🤖 Заражённый искином [${owner.name}]: призвал ещё одного`);
  }
  return { ok: true };
});
registerImplementedAbility('deus_zarazheny_iskinom', 'order');

// Mind-control orders.
// === Двойной агент (deus_dvoynoy_agent) — mind_control одного вражеского ===
registerC1Order('deus_dvoynoy_agent', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId === source.ownerId) return { ok: false, reason: 'invalid_target' };
  return mindControlUnit(state, source.ownerId, t);
});
registerImplementedAbility('deus_dvoynoy_agent', 'order');

// === Захват контракта (deus_zahvat_kontrakta) — mind_control_temporary ===
// 3 хода — упрощённо: перманентный угон. Полная семантика (вернуть через 3 хода)
// требует расширения системы.
registerC1Order('deus_zahvat_kontrakta', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId === source.ownerId) return { ok: false, reason: 'invalid_target' };
  return mindControlUnit(state, source.ownerId, t);
});
registerImplementedAbility('deus_zahvat_kontrakta', 'order');

// === Враждебное поглощение (deus_vrazhdebnoe_pogloshchenie) ===
// mind_control_mass: до 3 вражеских юнитов с атакой ≤3.
registerC1Order('deus_vrazhdebnoe_pogloshchenie', (state, source, opts) => {
  const enemy = state.players?.[1 - source.ownerId];
  if (!enemy?.field) return { ok: false };
  const candidates = [];
  for (const row of enemy.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive() && (u.current?.atk ?? 99) <= 3) candidates.push(u);
  }
  let captured = 0;
  for (let i = 0; i < 3 && candidates.length > 0; i++) {
    const idx = state.rng?.int ? state.rng.int(candidates.length) : 0;
    const [u] = candidates.splice(idx, 1);
    const result = mindControlUnit(state, source.ownerId, u);
    if (result.ok) captured++;
    else break; // нет свободных слотов.
  }
  return { ok: true, captured };
});
registerImplementedAbility('deus_vrazhdebnoe_pogloshchenie', 'order');

// === Перевербовка (deus_pereverbovka) ===
// composite: destroy:1@ally_choose | mind_control:atk_limit:5@enemy_choose
registerC1Order('deus_pereverbovka', (state, source, opts) => {
  // Сначала уничтожаем своего юнита (если указан в opts.ally).
  const ally = opts?.ally;
  if (ally && ally.ownerId === source.ownerId && ally.isAlive && ally.isAlive()) {
    applyDamage(ally, 999, { state, source });
  }
  // Затем mind_control вражеского с atk ≤ 5.
  const enemy = opts?.target;
  if (!enemy || enemy.ownerId === source.ownerId) return { ok: false, reason: 'invalid_target' };
  if ((enemy.current?.atk ?? 99) > 5) return { ok: false, reason: 'too_strong' };
  return mindControlUnit(state, source.ownerId, enemy);
});
registerImplementedAbility('deus_pereverbovka', 'order');

// === Наёмник (deus_naemnik) — return_to_hand_enemy ===
// Семантика: вражеский юнит возвращается в руку врага.
registerC1Order('deus_naemnik', (state, source, opts) => {
  const t = opts?.target;
  if (!t || t.ownerId === source.ownerId) return { ok: false, reason: 'invalid_target' };
  // По тексту карты — только юнит с атакой ≤ 2 (safety net; пикер уже фильтрует).
  if ((t.current?.atk ?? t.atk ?? 0) > 2) return { ok: false, reason: 'target_atk_too_high' };
  const enemyIdx = t.ownerId;
  const enemy = state.players?.[enemyIdx];
  if (!enemy) return { ok: false };
  // Снять с поля, base/current → reset, в руку (с лимитом 10 → в сброс если переполнено).
  if (t.line != null && enemy.field[t.line]?.[t.slot] === t) {
    enemy.field[t.line][t.slot] = null;
  }
  t.line = null; t.slot = null;
  // Восстановить current → base.
  if (t.current && t.base) {
    t.current.hp = t.base.hp;
    t.current.armor = t.base.armor;
    t.current.atk = t.base.atk;
    t.current.silenced = false;
  }
  t.placedTurn = null;
  t.revealed = false;
  if (enemy.hand.length >= 10) {
    enemy.discard.push(t);
  } else {
    enemy.hand.push(t);
  }
  return { ok: true };
});
registerImplementedAbility('deus_naemnik', 'order');

// === Председатель Танаки (deus_predsedatel_tanaki) — double_influence ===
registerC1Order('deus_predsedatel_tanaki', (state, source) => {
  const p = state.players?.[source.ownerId];
  if (!p) return { ok: false };
  const cur = p.influence || 0;
  addInfluence(state, source.ownerId, cur, source.name_ru);
  return { ok: true };
});
registerImplementedAbility('deus_predsedatel_tanaki', 'order');

// === Захват рынка (deus_zahvat_rynka) — free_summon_from_influence_pool 2 ===
// Семантика: 2 свободных призыва из стенда Влияния (упрощённо: даём 2 свободных
// слота в pendingPlacements или ставим флаг p.freeSummonsRemaining=2).
registerC1Order('deus_zahvat_rynka', (state, source) => {
  const p = state.players?.[source.ownerId];
  if (!p) return { ok: false };
  p.freeSummonsRemaining = (p.freeSummonsRemaining || 0) + 2;
  state.log?.add(`💼 Захват рынка: +2 бесплатных призыва (флаг)`);
  return { ok: true };
});
registerImplementedAbility('deus_zahvat_rynka', 'order');

// === Mega-sdelka (deus_mega_sdelka) — summon Korpoohranik + 50% скидка на 3 призыва ===
registerC1Order('deus_mega_sdelka', (state, source) => {
  const slot = findFreeSlot(state.players?.[source.ownerId]);
  if (slot) summonUnit(state, source.ownerId, 'deus_korpoohranik', slot.line, slot.slot);
  const p = state.players?.[source.ownerId];
  if (p) {
    p.nextSummonsDiscount = (p.nextSummonsDiscount || 0) + 3;
    p.nextSummonsDiscountPct = 50;
  }
  return { ok: true };
});
registerImplementedAbility('deus_mega_sdelka', 'order');

// === Великая жертва (deus_velikaya_zhertva) ===
// Это ACTION-карта — реальная логика в c1/actions-c1.js resolveVelikayaZhertva
// (диспатч через resolveActionC1, с opts.selectedInstanceIds от UI-мультиселекта).
// Прежний registerC1Order здесь был МЁРТВЫМ дублем (action-карты не проходят
// через fireC1Order + фильтр был ошибочный atk≤2 вместо atk≥2) — удалён.
registerImplementedAbility('deus_velikaya_zhertva', 'order');

// === Заражённый ИИскином (deus_zarazheny_iskinom) — vanilla unit, Order не нужен ===
// Order target=self/effect=summon мы трактуем как «карта сама ставится на поле» —
// это базовое размещение, не нужно дополнительной логики.
registerImplementedAbility('deus_inf_spyashy_agent', 'order');

// ============================================================
// === TRIGGERS                                                ===
// ============================================================

// === ally_died → buff_self / draw / influence ===
const ALLY_DIED_TRIGGERS = {
  'deus_bezymyany_ispolnitel': (state, u) => {
    applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 0 }, u.id, u.name_ru);
  },
  'deus_korpakula': (state, u) => {
    applyPermanentBuff(state, u, { atk: 0, armor: 0, hp: 2 }, u.id, u.name_ru);
  },
  'deus_arhiv_deusa': (state, u) => {
    applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 0 }, u.id, u.name_ru);
    // + random ally +1/0/0.
    const owner = state.players?.[u.ownerId];
    if (!owner?.field) return;
    const allies = [];
    for (const row of owner.field) for (const a of row || []) {
      if (a && a !== u && a.isAlive && a.isAlive()) allies.push(a);
    }
    if (allies.length === 0) return;
    const idx = state.rng?.int ? state.rng.int(allies.length) : Math.floor(Math.random() * allies.length);
    applyPermanentBuff(state, allies[idx], { atk: 1, armor: 0, hp: 0 }, u.id, u.name_ru);
  },
  'deus_inf_kladbishche_kontraktov': (state, u) => {
    const owner = state.players?.[u.ownerId];
    if (owner?.deck?.length > 0 && owner.hand.length < 10) {
      owner.hand.push(owner.deck.shift());
    }
  },
  'deus_vedomost_poter': (state, u) => {
    const owner = state.players?.[u.ownerId];
    if (owner?.deck?.length > 0 && owner.hand.length < 10) {
      owner.hand.push(owner.deck.shift());
    }
    applyPermanentBuff(state, u, { atk: 0, armor: 1, hp: 0 }, u.id, u.name_ru);
  },
  'deus_temnaya_vdova_sina': (state, u) => {
    applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 1 }, u.id, u.name_ru);
    addInfluence(state, u.ownerId, 1, u.name_ru);
  }
};

registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead) return;
  const ownerIdx = payload.ownerIdx;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u === dead) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u.current?.silenced) continue;
    const fn = ALLY_DIED_TRIGGERS[u.id];
    if (fn) fn(state, u);
  }
});
for (const id of Object.keys(ALLY_DIED_TRIGGERS)) {
  registerImplementedAbility(id, 'trigger');
}

// === Этап 56-fix: Куратор зачисток — aura on_ally_died (был stub) ===
// «Пока жив, при смерти любого союзника: 1 урон Герою врага И случайный
// союзник получает +0/1/1 навсегда (если союзников нет — на самого Куратора)».
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead) return;
  const ownerIdx = payload.ownerIdx;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  let kurator = null;
  for (const row of owner.field) for (const u of row || []) {
    if (u && u.id === 'deus_kurator_zachistok' && u !== dead
      && u.isAlive && u.isAlive() && !u.current?.silenced) { kurator = u; break; }
  }
  if (!kurator) return;
  // 1 урон Герою врага.
  const enemyHero = state.players?.[1 - ownerIdx]?.hero;
  if (enemyHero && enemyHero.hp > 0) applyDamage(enemyHero, 1, { state, source: kurator });
  // Случайный живой союзник +0/1/1 (fallback — сам Куратор).
  const allies = [];
  for (const row of owner.field) for (const u of row || []) {
    if (u && u !== dead && u.isAlive && u.isAlive()) allies.push(u);
  }
  const target = allies.length > 0
    ? allies[state.rng?.int ? state.rng.int(allies.length) : 0]
    : kurator;
  if (target) applyPermanentBuff(state, target, { atk: 0, armor: 1, hp: 1 }, kurator.id, kurator.name_ru);
  state.log?.add?.(`🧹 Куратор зачисток [${owner.name}]: смерть ${dead.name_ru} → 1 урон Герою врага + ${target?.name_ru} +0/1/1`);
});

// === ally_summoned → influence_gain ===
registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  if (!card || card.ownerId == null) return;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u === card) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u.current?.silenced) continue;
    if (u.id === 'deus_fin_razvedchik' || u.id === 'deus_inf_lichny_sovetnik') {
      addInfluence(state, ownerIdx, 1, u.name_ru);
    }
  }
});
registerImplementedAbility('deus_fin_razvedchik', 'trigger');
registerImplementedAbility('deus_inf_lichny_sovetnik', 'trigger');

// === turn_start ===
registerOnTurnStartExtraHandler((state) => {
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u || !u.isAlive || !u.isAlive()) continue;
      if (u.current?.silenced) continue;
      if (u.id === 'deus_bunker_kirosy') {
        const hero = p.hero;
        if (hero && hero.hp < (hero.maxHp || hero.base?.hp || hero.hp)) healUnit(state, hero, 1, u);
      }
      if (u.id === 'deus_lobbist') {
        addInfluence(state, i, 1, u.name_ru);
      }
      // Этап 56-fix: Совет директоров — aura turn_start +2 Влияния (был stub).
      if (u.id === 'deus_sovet_directorov') {
        addInfluence(state, i, 2, u.name_ru);
      }
      // QA-фикс: Знаток искинов (turn_start summon) был зарегистрирован ДВАЖДЫ —
      // здесь И в c1/influence-c1.js (registerOnTurnStartExtraHandler). Это давало
      // 2 призыва Заражённого за ход вместо 1. Оставлен только обработчик в
      // influence-c1.js (с логом). Класс «двойная регистрация триггера» (Этап 56).
    }
  }
});
registerImplementedAbility('deus_bunker_kirosy', 'trigger');
registerImplementedAbility('deus_lobbist', 'trigger');

// === turn_end → summon Stazher (deus_arhiv_deusa trigger2) ===
registerOnEndOfTurnExtraHandler((state) => {
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    if (!p?.field) continue;
    for (const row of p.field) for (const u of row || []) {
      if (!u || u.id !== 'deus_arhiv_deusa') continue;
      if (!u.isAlive || !u.isAlive()) continue;
      if (u.current?.silenced) continue;
      const slot = findFreeSlot(p);
      if (slot) summonUnit(state, i, STAZHER_ID, slot.line, slot.slot);
    }
  }
});

// === unit_attack triggers ===
registerOnAttackTrigger('deus_dvoynik_nikosi', (state, attacker) => {
  if (!state?.players) return;
  if (!attacker || attacker.current?.silenced) return;
  const p = state.players[attacker.ownerId];
  if (p) p.heroBonusAttackPending = (p.heroBonusAttackPending || 0) + 1;
});
registerImplementedAbility('deus_dvoynik_nikosi', 'trigger');

// БАГ-фикс (Этап 56.50): Личный палач был зарегистрирован на on-attack ДВАЖДЫ
// (здесь applyDamage 2 + в influence-c1.js applyHeroDamage 2) → бил Героя на 4
// вместо 2. Дубль убран — оставлена регистрация в influence-c1.js (правильный
// applyHeroDamage: учитывает броню/Иммунитет/Парашют).
registerImplementedAbility('deus_inf_lichny_palach', 'trigger');

registerOnAttackTrigger('deus_snayper_razvedchik', (state, attacker) => {
  if (!state?.players) return;
  if (!attacker || attacker.current?.silenced) return;
  addInfluence(state, attacker.ownerId, 2, attacker.name_ru);
});
registerImplementedAbility('deus_snayper_razvedchik', 'trigger');

// === unit_takes_damage → silence attacker (deus_podmazany_stukach) ===
registerOnDamageTakenTrigger('deus_podmazany_stukach', (state, target, dmg, _armor, attacker) => {
  if (!state?.players) return;
  if (dmg <= 0) return;
  // Этап 56.55 (решение фаундера «лучше под корпу/лор»): молчим именно
  // АТАКУЮЩЕГО (по тексту карты), если источник урона — живой вражеский юнит.
  if (attacker && attacker.type === 'unit'
      && attacker.ownerId === (1 - target.ownerId)
      && attacker.isAlive && attacker.isAlive()) {
    applySilence(state, attacker, target.id);
    return;
  }
  // Fallback: урон без юнита-источника (AoE-Действие, DoT) — случайный враг.
  const enemy = state.players[1 - target.ownerId];
  if (!enemy?.field) return;
  const enemies = [];
  for (const row of enemy.field) for (const u of row || []) {
    if (u && u.isAlive && u.isAlive()) enemies.push(u);
  }
  if (enemies.length === 0) return;
  const idx = state.rng?.int ? state.rng.int(enemies.length) : Math.floor(Math.random() * enemies.length);
  applySilence(state, enemies[idx], target.id);
});
registerImplementedAbility('deus_podmazany_stukach', 'trigger');


// ============================================================
// === DEATHRATTLES                                            ===
// ============================================================

// === enemy_died → mind_control random (Куратор операций) ===
// Триггер срабатывает на ВРАЖЕСКОГО мёртвого юнита. Куратор должен быть
// на стороне противоположной мёртвому. Угоняет случайного оставшегося
// вражеского юнита (с поля Куратора) на свою сторону.
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead || payload.ownerIdx == null) return;
  const kuratorIdx = 1 - payload.ownerIdx;
  const kuratorOwner = state.players?.[kuratorIdx];
  if (!kuratorOwner?.field) return;
  // Проверяем есть ли Куратор на поле и активен ли.
  let hasKurator = false;
  for (const row of kuratorOwner.field) for (const u of row || []) {
    if (u && u.id === 'deus_inf_kurator_operatsiy' && u.isAlive && u.isAlive() && !u.current?.silenced) {
      hasKurator = true; break;
    }
  }
  if (!hasKurator) return;
  // Цели — оставшиеся вражеские юниты Куратора (т.е. сторона payload.ownerIdx),
  // кроме мёртвой карты и спящих агентов.
  const enemyOwner = state.players[payload.ownerIdx];
  if (!enemyOwner?.field) return;
  const candidates = [];
  for (const row of enemyOwner.field) for (const u of row || []) {
    if (!u || u === dead) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u.sleeperState != null) continue;
    candidates.push(u);
  }
  if (candidates.length === 0) return;
  // Проверяем что у Куратора есть свободный слот.
  let hasFreeSlot = false;
  for (let l = 0; l < 2 && !hasFreeSlot; l++) {
    for (let s = 0; s < 4; s++) if (kuratorOwner.field[l][s] === null) { hasFreeSlot = true; break; }
  }
  if (!hasFreeSlot) return;
  const idx = state.rng?.int ? state.rng.int(candidates.length) : Math.floor(Math.random() * candidates.length);
  mindControlUnit(state, kuratorIdx, candidates[idx]);
});
registerImplementedAbility('deus_inf_kurator_operatsiy', 'trigger');

// === Deathrattles Деуса ===
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead) return;
  if (dead.current?.silenced) return;
  if (dead.id === 'deus_finansovy_analitik') {
    addInfluence(state, payload.ownerIdx, 2, dead.name_ru);
    const owner = state.players?.[payload.ownerIdx];
    if (owner?.deck?.length > 0 && owner.hand.length < 10) {
      owner.hand.push(owner.deck.shift());
    }
  }
  if (dead.id === 'deus_specagent') {
    addInfluence(state, payload.ownerIdx, 1, dead.name_ru);
  }
  if (dead.id === 'deus_zarazheny_iskinom') {
    const enemy = state.players?.[1 - payload.ownerIdx];
    if (!enemy) return;
    const pool = [];
    if (enemy.field) for (const row of enemy.field) for (const u of row || []) {
      if (u && u.isAlive && u.isAlive()) pool.push(u);
    }
    if (enemy.hero && enemy.hero.hp > 0) pool.push(enemy.hero);
    if (pool.length === 0) return;
    const idx = state.rng?.int ? state.rng.int(pool.length) : Math.floor(Math.random() * pool.length);
    applyDamage(pool[idx], 1, { state, source: dead });
  }
});
registerImplementedAbility('deus_finansovy_analitik', 'deathrattle');
registerImplementedAbility('deus_specagent', 'deathrattle');
registerImplementedAbility('deus_zarazheny_iskinom', 'deathrattle');

// ============================================================
// === AURAS                                                   ===
// ============================================================

// deus_bunker_kirosy aura — increase_max_hp:2 hero_self.
// Реализация: разовое применение при выставлении (см. trigger turn_start выше для лечения).
// Для max_hp используем self-buff на Героя при размещении карты:
registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  if (!card || card.id !== 'deus_bunker_kirosy') return;
  const hero = state.players?.[ownerIdx]?.hero;
  if (hero && !card.bunkerKirosyApplied) {
    hero.base.hp += 2;
    hero.current.hp += 2;
    card.bunkerKirosyApplied = true;
  }
});
registerImplementedAbility('deus_bunker_kirosy', 'aura');

// deus_lichnaya_gvardiya aura — hero_self +1/0/0 пока на поле.
registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  if (!card || card.id !== 'deus_lichnaya_gvardiya') return;
  const hero = state.players?.[ownerIdx]?.hero;
  if (hero && !card.lichnayaGvardiyaApplied) {
    hero.base.atk += 1;
    hero.current.atk += 1;
    card.lichnayaGvardiyaApplied = true;
  }
});
registerOnUnitDiedExtraHandler((state, payload) => {
  if (!payload?.card || payload.card.id !== 'deus_lichnaya_gvardiya') return;
  if (!payload.card.lichnayaGvardiyaApplied) return;
  const hero = state.players?.[payload.ownerIdx]?.hero;
  if (hero) {
    hero.base.atk = Math.max(0, hero.base.atk - 1);
    hero.current.atk = Math.max(0, hero.current.atk - 1);
  }
  payload.card.lichnayaGvardiyaApplied = false;
});
registerImplementedAbility('deus_lichnaya_gvardiya', 'aura');

// Любовница Танаки — free-summon реализована в influence-c1.js (effectiveInfluenceCost).
// Куратор зачисток / Совет директоров — реализованы ниже/в actions-c1 (стаб = warn-suppress).
registerImplementedAbility('deus_inf_lyubovnitsa_tanaki', 'aura');
registerImplementedAbility('deus_kurator_zachistok', 'aura');
registerImplementedAbility('deus_sovet_directorov', 'aura');

// === Двойник Киросы — immune-аура Герою (БАГ-фикс Этап 56.52) ===
// Раньше была застаблена (registerImplementedAbility без логики) → Герой НЕ
// получал Иммунитет. Текст: пока юнит жив, Герой имеет Иммунитет (1 удар
// поглощается полностью; восстанавливается в Ф1 каждого хода).
const DVOYNIK_IMMUNE_SRC = 'deus_inf_dvoynik_kirosy';
function deusDvoynikAlive(p) {
  return !!p?.field?.some?.(row => row?.some?.(u => u?.id === DVOYNIK_IMMUNE_SRC && u.isAlive?.()));
}
function heroHasDvoynikImmune(hero) {
  return Array.isArray(hero?.immuneSources) && hero.immuneSources.some(s => s.sourceId === DVOYNIK_IMMUNE_SRC);
}
// При призыве/выставлении Двойника — Герой получает Иммунитет.
registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  if (card?.id !== DVOYNIK_IMMUNE_SRC) return;
  const hero = state.players?.[ownerIdx]?.hero;
  if (hero && !heroHasDvoynikImmune(hero)) applyImmuneFirstHit(state, hero, DVOYNIK_IMMUNE_SRC);
});
// В начале хода — регенерация Иммунитета, пока Двойник жив (если был поглощён).
registerOnTurnStartExtraHandler((state) => {
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    if (deusDvoynikAlive(p) && p.hero && !heroHasDvoynikImmune(p.hero)) {
      applyImmuneFirstHit(state, p.hero, DVOYNIK_IMMUNE_SRC);
    }
  }
});
// При смерти Двойника — снять Иммунитет (если не осталось другого Двойника).
registerOnUnitDiedExtraHandler((state, payload) => {
  if (payload?.card?.id !== DVOYNIK_IMMUNE_SRC) return;
  const p = state.players?.[payload.ownerIdx];
  if (p?.hero && !deusDvoynikAlive(p)) removeImmunity(state, p.hero, DVOYNIK_IMMUNE_SRC);
});
registerImplementedAbility('deus_inf_dvoynik_kirosy', 'aura');

// ============================================================
// === VANILLA / простые карты                                 ===
// ============================================================
for (const id of [
  'deus_korpdiversant', 'deus_korpoohranik', 'deus_menedzher',
  'deus_shpion', 'deus_specnaz', 'deus_starshiy_shpion', 'deus_stazher',
  'deus_inf_elite_strelok',
  'deus_inf_korpgvardiya', 'deus_inf_korpshpion', 'deus_inf_tyazhely_dron'
]) {
  registerImplementedAbility(id, 'passive');
}
